/**
 * Details Loqate returns when its API rejects a request. The error item comes
 * back inside a normal 200 response, and react-loqate turns it into a
 * `LoqateError` — a class it doesn't export, so the only way to recognise one
 * is by the properties it copies off that item.
 */
export type LoqateErrorDetails = {
  code: string;
  cause: string;
  description: string;
  resolution: string;
};

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value !== "";
}

/**
 * Returns Loqate's own diagnostics when `error` is an API error, or `null` for
 * anything else (a render crash, a network failure, a bug of ours).
 */
export function getLoqateErrorDetails(
  error: unknown,
): LoqateErrorDetails | null {
  if (!(error instanceof Error)) {
    return null;
  }

  const candidate = error as Error & Record<string, unknown>;

  // `Cause` and `Resolution` together are specific enough: no other error we
  // see carries both, and `Error` alone would collide with the standard
  // `error.cause` property.
  if (
    !isNonEmptyString(candidate.Cause) ||
    !isNonEmptyString(candidate.Resolution)
  ) {
    return null;
  }

  return {
    code: isNonEmptyString(candidate.Error) ? candidate.Error : "",
    cause: candidate.Cause,
    description: isNonEmptyString(candidate.Description)
      ? candidate.Description
      : error.message,
    resolution: candidate.Resolution,
  };
}

const NETWORK_FAILURE_MESSAGES = [
  "failed to fetch", // Chrome, Edge
  // Safari. `EXTENSION_ERROR_PATTERNS` in error-monitoring.ts matches this
  // same wording, so beforeSend still drops the event: Safari lost requests
  // are classified here but do not reach Sentry yet. Read an empty iOS slice
  // as "not reported", not as "does not happen".
  "load failed",
  "networkerror when attempting to fetch resource", // Firefox
];

export function isNetworkError(error: unknown): boolean {
  if (!(error instanceof TypeError)) {
    return false;
  }

  const message = error.message.toLowerCase();

  return NETWORK_FAILURE_MESSAGES.some((failure) => message.includes(failure));
}
